import React, { useEffect, useState } from 'react';
import api from '../../api/axios';

interface Props {
  dealId: number;
  documentType: 'estimate' | 'contract' | 'act';
  onClose: () => void;
  onSuccess: () => void;
}

type Terms = {
  advanceAmount: number | '';
  advanceDays: number;
  finalDays: number;
  startDays: number;
  workDays: number;
  devices: number;
  archiveDays: number;
};

const DEFAULT_TERMS: Terms = { advanceAmount: '', advanceDays: 3, finalDays: 3, startDays: 3, workDays: 5, devices: 2, archiveDays: 14 };

const LABELS = {
  estimate: { title: 'Смета (Приложение № 1)', desc: 'Товары и услуги берутся из сделки: товары — в раздел «Оборудование», услуги — в «Работы».' },
  contract: { title: 'Договор', desc: 'Паспортные данные клиент заполнит сам в личном кабинете при первом открытии договора, там же подпишет.' },
  act: { title: 'Акт приёма-передачи (Приложение № 2)', desc: 'Оборудование — из сделки, аванс и окончательный расчёт — из платежей сделки.' },
};

const TERM_FIELDS: { key: keyof Terms; label: string; types: Props['documentType'][] }[] = [
  { key: 'advanceAmount', label: 'Аванс, ₸', types: ['estimate', 'contract'] },
  { key: 'advanceDays', label: 'Оплата аванса, кал. дней', types: ['contract'] },
  { key: 'finalDays', label: 'Окончат. расчёт, кал. дней', types: ['contract'] },
  { key: 'startDays', label: 'Начало работ, раб. дней', types: ['contract'] },
  { key: 'workDays', label: 'Срок работ, раб. дней', types: ['contract'] },
  { key: 'devices', label: 'Устройств для просмотра', types: ['contract'] },
  { key: 'archiveDays', label: 'Глубина архива, суток', types: ['estimate'] },
];

export default function CreateContractModal({ dealId, documentType, onClose, onSuccess }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [objectAddress, setObjectAddress] = useState('');
  const [terms, setTerms] = useState<Terms>(DEFAULT_TERMS);
  const [total, setTotal] = useState(0);
  const [dealPrepay, setDealPrepay] = useState(0);

  useEffect(() => {
    Promise.all([api.get(`/deals/${dealId}`), api.get(`/documents/by-deal/${dealId}`)])
      .then(([dealRes, docsRes]) => {
        const deal = dealRes.data ?? {};
        const dealTotal = Number(deal.amount) || 0;
        setTotal(dealTotal);
        setObjectAddress(deal.objectAddress ?? '');
        const docs: any[] = docsRes.data ?? [];
        const own = docs.find(d => d.type === documentType);
        const contract = docs.find(d => d.type === 'contract');
        const saved = { ...(own?.meta?.terms ?? {}), ...(contract?.meta?.terms ?? {}) };
        const prepay = Number(deal.prepayment) || 0;
        setDealPrepay(prepay);
        // «Предоплата» сделки — главный источник аванса
        const advanceAmount: number | '' = prepay > 0
          ? prepay
          : typeof saved.advanceAmount === 'number' ? saved.advanceAmount : '';
        setTerms({ ...DEFAULT_TERMS, ...saved, advanceAmount });
        if (own?.meta?.docDate) setDate(own.meta.docDate);
      })
      .catch(() => setError("Не удалось загрузить данные сделки. Проверьте поля вручную."));
  }, [dealId, documentType]);

  // Esc закрывает окно; клик по фону не закрывает, чтобы не потерять введённое
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !loading) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [loading, onClose]);

  const handleGenerate = async () => {
    if (documentType !== 'act') {
      if (terms.advanceAmount === '') {
        setError('Укажите сумму аванса в тенге (если аванса нет — 0)');
        return;
      }
      if (total > 0 && terms.advanceAmount > total) {
        setError(`Аванс не может быть больше суммы сделки (${total.toLocaleString('ru-RU')} ₸)`);
        return;
      }
    }
    setLoading(true);
    setError('');
    try {
      const visibleTerms = Object.fromEntries(
        TERM_FIELDS.filter(f => f.types.includes(documentType)).map(f => [f.key, Number(terms[f.key]) || 0]),
      );
      // аванс, введённый здесь, становится «Предоплатой» сделки — чтобы все документы и окно сделки показывали одно и то же
      if (documentType !== 'act' && terms.advanceAmount !== '' && Number(terms.advanceAmount) !== dealPrepay) {
        await api.put(`/deals/${dealId}`, { prepayment: Number(terms.advanceAmount) });
      }
      await api.post(`/documents/generate/${dealId}`, {
        type: documentType,
        docDate: date,
        objectAddress,
        ...(Object.keys(visibleTerms).length ? { terms: visibleTerms } : {}),
      });
      onSuccess();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Неизвестная ошибка');
    } finally {
      setLoading(false);
    }
  };

  const label = LABELS[documentType];
  const termFields = TERM_FIELDS.filter(f => f.types.includes(documentType));

  return (
    <div style={styles.overlay}>
      <div style={styles.modal} role="dialog" aria-modal="true" aria-labelledby="contract-modal-title">
        <div style={styles.header}>
          <h2 id="contract-modal-title" style={{ margin: 0, fontSize: 18, color: "#e5e7eb" }}>{label.title}</h2>
          <button onClick={onClose} style={styles.closeBtn} aria-label="Закрыть">✕</button>
        </div>

        <div style={styles.content}>
          <p style={styles.desc}>{label.desc}</p>

          <div style={styles.formGroup}>
            <label htmlFor="cm-date" style={styles.label}>Дата документа</label>
            <input id="cm-date" type="date" value={date} onChange={e => setDate(e.target.value)} style={styles.input} />
          </div>

          <div style={styles.formGroup}>
            <label htmlFor="cm-address" style={styles.label}>Адрес объекта</label>
            <input
              id="cm-address"
              value={objectAddress}
              onChange={e => setObjectAddress(e.target.value)}
              placeholder="Карагандинская обл., г. Темиртау, ул. …, д. …, кв. …"
              style={styles.input}
            />
            <div style={styles.hint}>Если оставить пустым — клиент укажет адрес в личном кабинете.</div>
          </div>

          {termFields.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
              {termFields.map(f => (
                <div key={f.key}>
                  <label htmlFor={`cm-${f.key}`} style={styles.label}>{f.label}</label>
                  <input
                    id={`cm-${f.key}`}
                    inputMode="numeric"
                    type="number" min={0}
                    value={terms[f.key]}
                    onChange={e => setTerms(t => ({ ...t, [f.key]: e.target.value === '' ? '' : Number(e.target.value) }))}
                    placeholder={f.key === 'advanceAmount' ? 'сумма в тенге' : undefined}
                    style={styles.input}
                  />
                  {f.key === 'advanceAmount' && total > 0 && (
                    <div style={styles.hint}>
                      Сумма сделки {total.toLocaleString('ru-RU')} ₸
                      {terms.advanceAmount !== '' && `, окончательный расчёт ${Math.max(0, total - terms.advanceAmount).toLocaleString('ru-RU')} ₸`}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {error && <div style={styles.error}>{Array.isArray(error) ? error.join(', ') : String(error)}</div>}

          <div style={styles.footer}>
            <button onClick={onClose} style={styles.cancelBtn}>Отмена</button>
            <button onClick={handleGenerate} disabled={loading} style={styles.generateBtn}>
              {loading ? 'Создание…' : '✓ Создать'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 },
  modal: { background: '#131926', borderRadius: 12, width: '95%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.5)', border: '1px solid #1f2937' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #1f2937' },
  closeBtn: { background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#9ca3af' },
  content: { padding: 24 },
  desc: { fontSize: 13, color: '#9ca3af', marginBottom: 20, lineHeight: 1.5 },
  formGroup: { marginBottom: 18 },
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: '#9ca3af', marginBottom: 6 },
  hint: { fontSize: 11, color: '#6b7280', marginTop: 4 },
  input: { width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid #374151', borderRadius: 8, background: '#1a2030', color: '#fff', fontSize: 14 },
  error: { background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171', borderRadius: 8, padding: '10px 12px', fontSize: 13, marginBottom: 16 },
  footer: { display: 'flex', gap: 12, justifyContent: 'flex-end', paddingTop: 16, borderTop: '1px solid #1f2937' },
  cancelBtn: { background: '#374151', color: '#e5e7eb', border: 'none', borderRadius: 8, padding: '10px 18px', cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  generateBtn: { background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', cursor: 'pointer', fontSize: 14, fontWeight: 600 },
};
